import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance';
import { POST_URL } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import {
  BulkSetPriorityRequest,
  BulkSetPriorityResponse
} from '@utils/types/back-office/priority-queue';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export const useBulkSetPriority = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const bulkSetPriority = async (payload: BulkSetPriorityRequest) => {
    const { data } = await Axios.post<BulkSetPriorityResponse>(
      POST_URL().bulkSetPriority,
      payload
    );
    return data;
  };

  return useMutation<
    BulkSetPriorityResponse,
    AxiosError<BaseError>,
    BulkSetPriorityRequest
  >(bulkSetPriority, {
    onSuccess: data => {
      const count = data.affected_count ?? 0;
      const skipped = data.skipped_count ?? 0;
      const skippedSuffix =
        skipped > 0
          ? ` • ${t('fair-use-skipped-count', { count: skipped })}`
          : '';
      toast.success(`${t('priority-updated')}: ${count}${skippedSuffix}`);
      queryClient.invalidateQueries(['priorityQueue']);
      queryClient.invalidateQueries(['queueSummary']);
    },
    onError: (error: AxiosError<BaseError>) => {
      const d = error.response?.data as Record<string, unknown>;
      const msg =
        (d?.error_description as string) ||
        (d?.detail as string) ||
        (d?.error as string) ||
        (d?.message as string) ||
        t('an-error-occurred');
      toast.error(String(msg));
      // A 409 means rows moved on under us; refetch so the list stops showing
      // the state the operator just acted on.
      queryClient.invalidateQueries(['priorityQueue']);
      queryClient.invalidateQueries(['queueSummary']);
    }
  });
};
